import { Capacitor } from '@capacitor/core';
import { Box, useTheme } from '@mui/material';
import { PropsWithChildren, useMemo } from 'react';

import { ConditionalWrap } from '@frontend-libs/atomic';
import { PortalTheme } from '@frontend-libs/portal';

import { useOptionalBottomNavbar } from './app-bottom-navbar-provider.component';
import { PageSlideAnimation } from './page-slide-animation.component';
import {
  BottomNavbarPaddingMode,
  getViewBottomPadding,
} from './view-bottom-padding';
import { ViewSection } from './view-section.component';

export interface ViewProps {
  header?: React.ReactNode;
  bottomNavbarPadding?: BottomNavbarPaddingMode;
  enableAnimation?: boolean;
  fullHeight?: boolean;
  disableScroll?: boolean;
}

export const View = ({
  enableAnimation = false,
  ...props
}: PropsWithChildren<ViewProps>) => {
  const theme = useTheme<PortalTheme>();
  const isNavbarVisible = useOptionalBottomNavbar()?.isVisible ?? false;
  const bottomPaddingValue = useMemo(() => {
    return getViewBottomPadding({
      bottomNavbarPadding: props.bottomNavbarPadding,
      isNavbarVisible,
      isNative: Capacitor.isNativePlatform(),
      bottomNavbarHeight: theme.bottomNavbarHeight,
    });
  }, [props.bottomNavbarPadding, isNavbarVisible, theme.bottomNavbarHeight]);

  return (
    <ConditionalWrap
      condition={enableAnimation}
      wrap={(children) => <PageSlideAnimation>{children}</PageSlideAnimation>}
    >
      <Box
        id="view"
        component="main"
        sx={{
          pb: bottomPaddingValue,
          width: '100vw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: props.disableScroll ? 'auto' : 'hidden',
          background: theme.palette.background.default,
        }}
      >
        {props.header && (
          <ViewSection id="view-header" sx={{ px: 0 }}>
            {props.header}
          </ViewSection>
        )}

        <Box
          id="view-children"
          sx={{
            height: props.fullHeight ? '100%' : 'auto',
            overflowY: props.disableScroll ? 'hidden' : 'auto',
            overflowX: 'hidden', // explicitly set this due to issues with carousel
            display: 'flex',
            flexDirection: 'column',
            isolation: 'isolate',
          }}
        >
          {props.children}
        </Box>
      </Box>
    </ConditionalWrap>
  );
};
